import { Injectable, inject } from '@angular/core';
import { catchError, of, switchMap, Observable, combineLatest, map } from 'rxjs';
import {
  FilterModel,
  FilterModels,
  ListingFiltersSource
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { EventConfigurationApiService } from '../event-configuration/services/event-configuration-api.service';
import { ReportGroupFilterApiService } from 'src/app/services/api/report-group-filter/report-group-filter-api.service';
import { Store } from '@ngrx/store';
import {
  isSingleCommodityClient,
  selectCommoditiesItems
} from '../../help-and-settings/user-settings/store/user-settings.reducer';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import { EventGroupsResponse } from '../event-configuration/services/event-configuration-api.type';
import { ReportGroupFilterResponse } from 'src/app/services/api/report-group-filter/report-group-filter-api.types';
import { MultiselectItem } from '@ui/aos-lib';
import {
  eventGroupFilterModel,
  reportGroupFilterModel,
  commodityFilterModel,
  severityFilterModel,
  channelFilterModel,
  deliveryFilterModel
} from './subscription-management.constants';
import { GroupStatus } from '../report-groups/report-groups.types';

@Injectable()
export class SubscriptionListingFiltersSourceService implements ListingFiltersSource {
  private readonly store = inject(Store);
  private readonly reportGroupFilterApiService = inject(ReportGroupFilterApiService);
  private readonly eventConfigurationApiService = inject(EventConfigurationApiService);
  readonly #isSingleCommodityClient = this.store.selectSignal(isSingleCommodityClient);

  readonly #applicable = 'Network Monitoring';

  getAll(): Observable<FilterModels> {
    return combineLatest([
      this.eventConfigurationApiService.getEventGroupsWithEventNames(),
      this.getReportGroup()
    ]).pipe(
      switchMap(
        ([eventGroupItems, reportGroupFilter]: [
          eventGroupItems: EventGroupsResponse[],
          reportGroupFilter: FilterModel
        ]) => {
          const eventGroupMultiselectItems: MultiselectItem[] = eventGroupItems.map(({ groupName }) => ({
            text: translationKeysMap.get(groupName) || groupName,
            value: groupName
          }));
          const eventGroupFilter: FilterModel = {
            ...eventGroupFilterModel,
            items: eventGroupMultiselectItems,
            selected: eventGroupMultiselectItems
          };
          const commodities = this.store.selectSignal(selectCommoditiesItems);
          const commodityFilter: FilterModel = {
            ...commodityFilterModel,
            items: commodities(),
            selected: commodities()
          };

          return of([
            eventGroupFilter,
            severityFilterModel,
            ...(this.#isSingleCommodityClient() ? [] : [commodityFilter]),
            reportGroupFilter,
            channelFilterModel,
            deliveryFilterModel
          ]);
        }
      ),
      catchError(error => {
        return of(error);
      })
    );
  }

  getReportGroup(): Observable<FilterModel> {
    return this.reportGroupFilterApiService.getReportGroup(this.#applicable).pipe(
      map((reportGroupItems: ReportGroupFilterResponse[]) => {
        const reportGroupMultiselectItems: MultiselectItem[] = reportGroupItems.map(
          ({ groupName, groupUID, groupStatus }) => ({
            text: groupName,
            value: groupUID,
            isDisabled: groupStatus === GroupStatus.Calculating
          })
        );
        return {
          ...reportGroupFilterModel,
          items: reportGroupMultiselectItems,
          selected: reportGroupMultiselectItems
        };
      })
    );
  }
}
